import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Users, Plus, Trophy, Loader2, Brain, Crown, ChevronRight,
  MessageCircle, Send, UserPlus, Mail, Check, X, Bell
} from 'lucide-react';
import { hasFeatureAccess } from '../utils/subscriptionFeatures';
import { LockedFeatureButton } from '../components/LockedFeature';

export default function GroupStudyPage({ onNavigate }) {
  const { api, user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [generatingChallenge, setGeneratingChallenge] = useState(false);
  const [challengeSubject, setChallengeSubject] = useState('Math');
  const [challengeChapter, setChallengeChapter] = useState(1);
  const [chapters, setChapters] = useState([]);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [activeTab, setActiveTab] = useState('challenges'); // 'challenges' | 'chat' | 'members'
  const chatEndRef = useRef(null);
  
  // Invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  
  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [showInvitations, setShowInvitations] = useState(false);

  const className = user?.class_name || 'Class 8';
  const subscription = user?.subscription || 'free';
  const subjects = ['Math', 'Science', 'English', 'Social Science', 'Hindi', 'Physics', 'Chemistry', 'Biology'];
  const canAccessGroupStudy = hasFeatureAccess(subscription, 'group_study');

  // Fetch chapters when subject changes
  const fetchChapters = useCallback(async () => {
    try {
      const res = await api.get(`/chapters?class_name=${encodeURIComponent(className)}&subject=${encodeURIComponent(challengeSubject)}`);
      // Sort chapters by number (NCERT curriculum order)
      const sortedChapters = (res.data || []).sort((a, b) => a.number - b.number);
      setChapters(sortedChapters);
      if (sortedChapters.length > 0) {
        setChallengeChapter(sortedChapters[0].number);
      }
    } catch (err) {
      console.error('Error fetching chapters:', err);
      setChapters([]);
    }
  }, [api, className, challengeSubject]);

  useEffect(() => { fetchChapters(); }, [fetchChapters]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/groups');
      setGroups(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await api.get('/groups/invitations');
      setInvitations(res.data);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => { 
    fetchGroups(); 
    fetchInvitations();
  }, [fetchGroups, fetchInvitations]);

  const createGroup = async () => {
    if (!groupName.trim()) return;
    setCreating(true);
    try {
      await api.post('/groups/create', { name: groupName, class_name: className });
      setGroupName('');
      fetchGroups();
    } catch (err) { console.error(err); }
    setCreating(false);
  };

  const joinGroup = async (groupId) => {
    try {
      await api.post('/groups/join', { group_id: groupId });
      fetchGroups();
    } catch (err) { console.error(err); }
  };

  const selectGroup = async (group) => {
    setSelectedGroup(group);
    setActiveTab('challenges');
    try {
      const [challengesRes, messagesRes] = await Promise.all([
        api.get(`/groups/${group.id}/challenges`),
        api.get(`/groups/${group.id}/chat`).catch(() => ({ data: [] }))
      ]);
      setChallenges(challengesRes.data);
      setMessages(messagesRes.data);
    } catch (err) { console.error(err); }
  };

  const createChallenge = async () => {
    if (!selectedGroup) return;
    setGeneratingChallenge(true);
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/challenge`, {
        group_id: selectedGroup.id,
        subject: challengeSubject,
        chapter_number: parseInt(challengeChapter),
        num_questions: 5
      });
      setChallenges(prev => [res.data, ...prev]);
    } catch (err) { console.error(err); }
    setGeneratingChallenge(false);
  };

  // Chat functions
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedGroup) return;
    setSendingMessage(true);
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/chat`, { message: newMessage });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error(err); }
    setSendingMessage(false);
  };

  const refreshMessages = async () => {
    if (!selectedGroup) return;
    try {
      const res = await api.get(`/groups/${selectedGroup.id}/chat`);
      setMessages(res.data);
    } catch (err) { console.error(err); }
  };

  // Auto-refresh chat every 10 seconds when chat tab is active
  useEffect(() => {
    if (activeTab === 'chat' && selectedGroup) {
      const interval = setInterval(refreshMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [activeTab, selectedGroup]);

  // Invite functions
  const inviteStudent = async () => {
    if (!inviteEmail.trim() || !selectedGroup) return;
    setInviting(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/invite`, { 
        group_id: selectedGroup.id,
        email: inviteEmail 
      });
      setInviteSuccess(res.data.message);
      setInviteEmail('');
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to send invitation');
    }
    setInviting(false);
  };

  const handleInvitation = async (inviteId, accept) => {
    try {
      if (accept) {
        await api.post(`/groups/invitations/${inviteId}/accept`);
      } else {
        await api.post(`/groups/invitations/${inviteId}/decline`);
      }
      fetchInvitations();
      fetchGroups();
    } catch (err) { console.error(err); }
  };

  // Group Detail View
  if (selectedGroup) {
    return (
      <div className="pb-nav px-4 pt-6 max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <button data-testid="back-from-group-detail" onClick={() => setSelectedGroup(null)} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="group-name">{selectedGroup.name}</h1>
            <p className="text-xs text-slate-500">{selectedGroup.members?.length} members - {selectedGroup.class_name}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {['challenges', 'chat', 'members'].map(tab => (
            <button
              key={tab}
              data-testid={`tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold transition-all capitalize ${
                activeTab === tab 
                  ? 'bg-violet-600 text-white' 
                  : 'bg-slate-800/50 text-slate-400 hover:text-white'
              }`}
            >
              {tab === 'chat' && <MessageCircle className="w-3 h-3 inline mr-1" />}
              {tab === 'members' && <Users className="w-3 h-3 inline mr-1" />}
              {tab === 'challenges' && <Trophy className="w-3 h-3 inline mr-1" />}
              {tab}
            </button>
          ))}
        </div>

        {/* Challenges Tab */}
        {activeTab === 'challenges' && (
          <>
            {/* Create Challenge */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 mb-4">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Brain className="w-4 h-4 text-amber-400" /> Create Challenge
              </h3>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <select data-testid="challenge-subject" value={challengeSubject} onChange={e => setChallengeSubject(e.target.value)}
                  className="bg-slate-950/50 border border-slate-800 rounded-xl h-10 px-3 text-sm text-slate-100 outline-none appearance-none">
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select data-testid="challenge-chapter" value={challengeChapter} onChange={e => setChallengeChapter(e.target.value)}
                  className="bg-slate-950/50 border border-slate-800 rounded-xl h-10 px-3 text-sm text-slate-100 outline-none appearance-none">
                  {chapters.map(ch => <option key={ch.number} value={ch.number}>Ch {ch.number}: {ch.name.length > 15 ? ch.name.substring(0, 15) + '...' : ch.name}</option>)}
                </select>
              </div>
              <button data-testid="create-challenge-btn" onClick={createChallenge} disabled={generatingChallenge}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-xl h-10 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {generatingChallenge ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                {generatingChallenge ? 'Generating...' : 'Start Challenge'}
              </button>
            </div>

            {/* Challenges List */}
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Challenges</h3>
            <div className="space-y-3">
              {challenges.map((ch, i) => (
                <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid={`challenge-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-white">{ch.chapter_name} - {ch.subject}</p>
                    <span className="text-xs text-slate-500">{ch.quiz?.questions?.length}Q</span>
                  </div>
                  {ch.scores && Object.keys(ch.scores).length > 0 && (
                    <div className="space-y-1 mb-2">
                      {Object.entries(ch.scores).sort((a, b) => b[1].score - a[1].score).map(([uid, s], j) => (
                        <div key={j} className="flex items-center justify-between bg-slate-800/30 rounded-lg px-2 py-1">
                          <span className="text-xs text-slate-300">{j === 0 ? '🥇' : j === 1 ? '🥈' : '🥉'} {s.name}</span>
                          <span className={`text-xs font-bold ${s.score >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{s.score}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!ch.scores?.[user.id] && (
                    <button
                      data-testid={`take-challenge-${i}`}
                      onClick={() => onNavigate('quiz', { quest: { ...ch, id: ch.id } })}
                      className="w-full bg-violet-600/10 border border-violet-500/20 text-violet-300 rounded-lg py-2 text-xs font-medium hover:bg-violet-600/20"
                    >
                      Take Challenge
                    </button>
                  )}
                </div>
              ))}
              {challenges.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-6">No challenges yet. Create one!</p>
              )}
            </div>
          </>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden flex flex-col" style={{ height: '400px' }}>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <MessageCircle className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No messages yet. Start the conversation!</p>
                </div>
              )}
              {messages.map((msg, i) => {
                const isMe = msg.sender_id === user.id;
                return (
                  <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] ${isMe ? 'bg-violet-600' : 'bg-slate-800'} rounded-2xl px-3 py-2`}>
                      {!isMe && (
                        <p className="text-[10px] text-violet-300 font-semibold mb-0.5">{msg.sender_name}</p>
                      )}
                      <p className="text-sm text-white break-words">{msg.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
            
            {/* Message Input */}
            <div className="p-3 border-t border-slate-800">
              <div className="flex gap-2">
                <input
                  data-testid="chat-input"
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 bg-slate-950/50 border border-slate-700 focus:border-violet-500 rounded-xl h-10 px-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none"
                />
                <button
                  data-testid="send-message-btn"
                  onClick={sendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-4 disabled:opacity-50 transition-all"
                >
                  {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <>
            {/* Invite Section */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 mb-4">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-emerald-400" /> Invite Student
              </h3>
              <div className="flex gap-2">
                <input
                  data-testid="invite-email-input"
                  type="email"
                  value={inviteEmail}
                  onChange={e => { setInviteEmail(e.target.value); setInviteError(''); setInviteSuccess(''); }}
                  placeholder="Enter student's email"
                  className="flex-1 bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-10 px-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none"
                />
                <button
                  data-testid="invite-btn"
                  onClick={inviteStudent}
                  disabled={inviting || !inviteEmail.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4 text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                >
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                </button>
              </div>
              {inviteError && (
                <p className="text-xs text-red-400 mt-2">{inviteError}</p>
              )}
              {inviteSuccess && (
                <p className="text-xs text-emerald-400 mt-2">{inviteSuccess}</p>
              )}
            </div>

            {/* Members List */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-violet-400" /> Members ({selectedGroup.members?.length})
              </h3>
              <div className="space-y-2">
                {selectedGroup.members?.map((m, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-800/30 rounded-lg px-3 py-2" data-testid={`member-${i}`}>
                    <div className="flex items-center gap-2">
                      {i === 0 && <Crown className="w-3 h-3 text-yellow-400" />}
                      <span className="text-sm text-white">{m.name}</span>
                      {m.id === user.id && <span className="text-[10px] bg-violet-500/20 text-violet-400 px-1.5 rounded">You</span>}
                    </div>
                    <span className="text-xs text-violet-400">{m.xp} XP</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Groups List View
  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="back-from-groups" onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="groups-title">Group Study</h1>
          <p className="text-xs text-slate-500">Compete with classmates</p>
        </div>
        {invitations.length > 0 && (
          <button
            data-testid="invitations-btn"
            onClick={() => setShowInvitations(!showInvitations)}
            className="relative bg-violet-600/20 text-violet-400 rounded-xl p-2"
          >
            <Bell className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {invitations.length}
            </span>
          </button>
        )}
      </div>

      {/* Pending Invitations */}
      <AnimatePresence>
        {showInvitations && invitations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-5"
          >
            <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" /> Pending Invitations
            </h3>
            <div className="space-y-2">
              {invitations.map(inv => (
                <div key={inv.id} className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                  <p className="text-sm text-white mb-1">{inv.group_name}</p>
                  <p className="text-xs text-slate-400 mb-2">Invited by {inv.inviter_name}</p>
                  <div className="flex gap-2">
                    <button
                      data-testid={`accept-invite-${inv.id}`}
                      onClick={() => handleInvitation(inv.id, true)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-1.5 text-xs font-medium flex items-center justify-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Accept
                    </button>
                    <button
                      data-testid={`decline-invite-${inv.id}`}
                      onClick={() => handleInvitation(inv.id, false)}
                      className="flex-1 bg-slate-700 hover:bg-slate-600 text-white rounded-lg py-1.5 text-xs font-medium flex items-center justify-center gap-1"
                    >
                      <X className="w-3 h-3" /> Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Group */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 mb-5">
        <h3 className="text-sm font-semibold text-white mb-3">Create or Join a Group</h3>
        <div className="flex gap-2">
          <input data-testid="group-name-input" type="text" value={groupName} onChange={e => setGroupName(e.target.value)}
            placeholder="Group name (e.g. Class 8A Math)"
            className="flex-1 bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-10 px-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none" />
          <LockedFeatureButton
            feature="group_study"
            subscription={subscription}
            onClick={createGroup}
            onUpgrade={() => onNavigate('subscription')}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-4 text-sm font-medium disabled:opacity-50 flex items-center gap-1"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </LockedFeatureButton>
        </div>
      </div>

      {/* Groups List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-violet-500 animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {groups.map(g => {
            const isMember = g.members?.some(m => m.id === user.id);
            return (
              <div key={g.id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 hover:border-violet-500/30 transition-all" data-testid={`group-${g.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-white">{g.name}</h3>
                    <p className="text-xs text-slate-500">{g.members?.length} members - {g.class_name}</p>
                  </div>
                  {isMember ? (
                    <button 
                      data-testid={`open-group-${g.id}`}
                      onClick={() => selectGroup(g)} 
                      className="bg-violet-600 text-white rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1"
                    >
                      Open <ChevronRight className="w-3 h-3" />
                    </button>
                  ) : (
                    <LockedFeatureButton
                      feature="group_study"
                      subscription={subscription}
                      onClick={() => joinGroup(g.id)}
                      onUpgrade={() => onNavigate('subscription')}
                      className="bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-xs font-medium"
                    >
                      Join
                    </LockedFeatureButton>
                  )}
                </div>
              </div>
            );
          })}
          {groups.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No groups yet. Create the first one!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
